/**
 * useCoachWorkflow Hook
 *
 * Manages AI Coach workflow execution using the backend workflow system.
 * Replaces direct Claude API calls with backend-driven prompts and SSE streaming.
 *
 * Features:
 * - SSE streaming for real-time responses
 * - Workflow step management
 * - MCQ follow-up generation
 * - Assessment tracking
 * - Entity extraction
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getSessionToken } from '@/lib/api/client'
import { useAIStore } from '@/stores/aiStore'
import { getCurrentAccountId, getCurrentProjectId } from '@/lib/api/sparql-adapter'

// ============================================================================
// TYPES
// ============================================================================

export interface CoachStep {
  key: string
  name: string
  description: string
  stepNumber: number
  color: string
  phase: string
  focusAreas: string[]
  qualitySignals: string[]
}

export interface CoachWorkflow {
  id: string
  key: string
  name: string
  description: string
  steps: CoachStep[]
}

export interface CoachAssessment {
  quality: string
  confidence: number
  feedback: string
  suggestions?: string[]
}

// Collaborative coaching feedback (v2)
export interface CoachingFeedback {
  acknowledge?: string
  strength?: string
  opportunity?: {
    prompt: string
    options?: Array<{ label: string; example?: string }>
  }
  readiness?: {
    canProceed: boolean
    score: number
  }
  feedback: string
  coachingStyle: 'collaborative'
}

// Facets created from extracted entities (v2)
export interface FacetsCreatedInfo {
  count: number
  facets: Array<{ facetId: string; sourceId: string; status: string }>
  message: string
}

export interface MCQOption {
  id: string
  label: string
  description?: string
}

export interface ExtractedInsights {
  entities: Array<{ type: string; label: string; description?: string }>
  insights: string[]
  keyPoints: string[]
}

export interface KnowledgeContext {
  chunksRetrieved: number
  sources: string[]
  topSimilarity?: number
}

export interface CoachState {
  currentStepKey: string
  currentStepNumber: number
  currentPhase: 'idle' | 'streaming' | 'awaiting_input' | 'complete'
  completedSteps: string[]
  lastAssessment?: CoachAssessment
  lastCoaching?: CoachingFeedback // v2 collaborative feedback
  lastMCQOptions?: MCQOption[]
  lastExtracted?: ExtractedInsights
  lastKnowledgeContext?: KnowledgeContext
  lastFacetsCreated?: FacetsCreatedInfo // v2 facets created
}

export interface StreamingState {
  isStreaming: boolean
  rawText: string
  eventType?: string
}

// ============================================================================
// API HELPERS
// ============================================================================

const API_URL = import.meta.env.VITE_API_URL ?? ''

async function fetchCoachWorkflow(): Promise<{ workflow: CoachWorkflow; steps: CoachStep[] }> {
  const accountId = getCurrentAccountId()
  const projectId = getCurrentProjectId()

  if (!accountId || !projectId) {
    throw new Error('Account and project context required')
  }

  const token = getSessionToken()
  const response = await fetch(
    `${API_URL}/api/accounts/${accountId}/projects/${projectId}/discovery/coach/workflow`,
    {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to fetch coach workflow: ${response.status}`)
  }

  return response.json()
}

// ============================================================================
// HOOK
// ============================================================================

export function useCoachWorkflow(sessionId?: string) {
  const { apiKey, isConfigured, coachingModel, coachingStyle, persistFacets, getEmbeddingConfig } = useAIStore()

  // State
  const [coachState, setCoachState] = useState<CoachState>({
    currentStepKey: 'understanding-context',
    currentStepNumber: 1,
    currentPhase: 'idle',
    completedSteps: [],
  })

  const [streamingState, setStreamingState] = useState<StreamingState>({
    isStreaming: false,
    rawText: '',
  })

  const [error, setError] = useState<Error | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const eventSourceRef = useRef<EventSource | null>(null)

  // Query for workflow configuration
  const {
    data: workflowData,
    isLoading: isLoadingWorkflow,
  } = useQuery({
    queryKey: ['coach-workflow'],
    queryFn: fetchCoachWorkflow,
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: isConfigured,
  })

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
      eventSourceRef.current?.close()
    }
  }, [])

  /**
   * Execute a coach step via SSE streaming
   */
  const executeStep = useCallback(
    async (
      stepKey: string,
      promptPhase: 'question' | 'assess' | 'followup' | 'extract',
      userInput?: string,
      previousMessages?: Array<{ role: string; content: string }>
    ) => {
      if (!isConfigured || !apiKey) {
        setError(new Error('AI not configured'))
        return
      }

      const accountId = getCurrentAccountId()
      const projectId = getCurrentProjectId()
      const effectiveSessionId = sessionId || 'standalone'

      if (!accountId || !projectId) {
        setError(new Error('Account and project context required'))
        return
      }

      // Abort any ongoing request
      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()

      setStreamingState({ isStreaming: true, rawText: '' })
      setCoachState((prev) => ({ ...prev, currentPhase: 'streaming' }))
      setError(null)

      try {
        const token = getSessionToken()
        const embeddingConfig = getEmbeddingConfig()

        const response = await fetch(
          `${API_URL}/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${effectiveSessionId}/coach/stream`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'text/event-stream',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: 'include',
            body: JSON.stringify({
              stepKey,
              promptPhase,
              userInput,
              previousMessages,
              apiKey,
              model: coachingModel,
              // Enable RAG knowledge retrieval
              enableKnowledgeRetrieval: true,
              embeddingConfig: embeddingConfig || undefined,
              // Coaching style (v2)
              coachingStyle,
              persistFacets,
            }),
            signal: abortControllerRef.current.signal,
          }
        )

        if (!response.ok) {
          throw new Error(`Coach stream error: ${response.status}`)
        }

        // Process SSE stream
        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('No response body')
        }

        const decoder = new TextDecoder()
        let buffer = ''
        let accumulatedText = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('event:')) {
              const eventType = line.slice(6).trim()
              setStreamingState((prev) => ({ ...prev, eventType }))
            } else if (line.startsWith('data:')) {
              const data = line.slice(5).trim()
              try {
                const parsed = JSON.parse(data)
                handleSSEEvent(parsed, eventType => {
                  setStreamingState(prev => ({ ...prev, eventType }))
                })
              } catch {
                // Non-JSON data, treat as text
                accumulatedText += data
                setStreamingState((prev) => ({
                  ...prev,
                  rawText: accumulatedText,
                }))
              }
            }
          }
        }

        setStreamingState((prev) => ({ ...prev, isStreaming: false }))
        setCoachState((prev) => ({ ...prev, currentPhase: 'awaiting_input' }))
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err)
          setCoachState((prev) => ({ ...prev, currentPhase: 'idle' }))
        }
        setStreamingState({ isStreaming: false, rawText: '' })
      }
    },
    [isConfigured, apiKey, coachingModel, coachingStyle, persistFacets, sessionId, getEmbeddingConfig]
  )

  /**
   * Handle SSE events from the backend
   */
  const handleSSEEvent = useCallback(
    (data: unknown, setEventType: (type: string) => void) => {
      if (!data || typeof data !== 'object') return

      const eventData = data as Record<string, unknown>

      // Text event - streaming response
      if ('content' in eventData && typeof eventData.content === 'string') {
        setStreamingState((prev) => ({
          ...prev,
          rawText: prev.rawText + eventData.content,
        }))
        return
      }

      // Assessment event (v1 directive style)
      if ('quality' in eventData && 'confidence' in eventData && !('coachingStyle' in eventData)) {
        const assessment: CoachAssessment = {
          quality: eventData.quality as string,
          confidence: eventData.confidence as number,
          feedback: (eventData.feedback as string) || '',
          suggestions: eventData.suggestions as string[] | undefined,
        }
        setCoachState((prev) => ({ ...prev, lastAssessment: assessment }))
        setEventType('assessment')
        return
      }

      // Coaching event (v2 collaborative style)
      if ('coachingStyle' in eventData && eventData.coachingStyle === 'collaborative') {
        const coaching: CoachingFeedback = {
          acknowledge: eventData.acknowledge as string | undefined,
          strength: eventData.strength as string | undefined,
          opportunity: eventData.opportunity as CoachingFeedback['opportunity'],
          readiness: eventData.readiness as CoachingFeedback['readiness'],
          feedback: (eventData.feedback as string) || '',
          coachingStyle: 'collaborative',
        }
        setCoachState((prev) => ({ ...prev, lastCoaching: coaching }))
        setEventType('coaching')
        return
      }

      // Facets created event (v2)
      if ('count' in eventData && 'facets' in eventData && 'message' in eventData) {
        const facetsCreated: FacetsCreatedInfo = {
          count: eventData.count as number,
          facets: eventData.facets as FacetsCreatedInfo['facets'],
          message: eventData.message as string,
        }
        setCoachState((prev) => ({ ...prev, lastFacetsCreated: facetsCreated }))
        setEventType('facets_created')
        return
      }

      // MCQ option event
      if ('id' in eventData && 'label' in eventData) {
        const option: MCQOption = {
          id: eventData.id as string,
          label: eventData.label as string,
          description: eventData.description as string | undefined,
        }
        setCoachState((prev) => ({
          ...prev,
          lastMCQOptions: [...(prev.lastMCQOptions || []), option],
        }))
        setEventType('mcq_option')
        return
      }

      // Knowledge context event (RAG retrieval results)
      if ('chunksRetrieved' in eventData && 'sources' in eventData) {
        const knowledgeContext: KnowledgeContext = {
          chunksRetrieved: eventData.chunksRetrieved as number,
          sources: eventData.sources as string[],
          topSimilarity: eventData.topSimilarity as number | undefined,
        }
        setCoachState((prev) => ({ ...prev, lastKnowledgeContext: knowledgeContext }))
        setEventType('knowledge_context')
        return
      }

      // Extract event
      if ('entities' in eventData || 'insights' in eventData) {
        const extracted: ExtractedInsights = {
          entities: (eventData.entities as Array<{ type: string; label: string; description?: string }>) || [],
          insights: (eventData.insights as string[]) || [],
          keyPoints: (eventData.keyPoints as string[]) || (eventData.key_points as string[]) || [],
        }
        setCoachState((prev) => ({ ...prev, lastExtracted: extracted }))
        setEventType('extract')
        return
      }
    },
    []
  )

  /**
   * Start a new step with the initial question
   */
  const startStep = useCallback(
    async (stepKey: string) => {
      setCoachState((prev) => ({
        ...prev,
        currentStepKey: stepKey,
        currentPhase: 'idle',
        lastAssessment: undefined,
        lastCoaching: undefined,
        lastMCQOptions: undefined,
        lastExtracted: undefined,
        lastFacetsCreated: undefined,
      }))

      await executeStep(stepKey, 'question')
    },
    [executeStep]
  )

  /**
   * Process user's answer - runs assess, followup, and extract
   */
  const processAnswer = useCallback(
    async (
      userAnswer: string,
      previousMessages?: Array<{ role: string; content: string }>
    ) => {
      const { currentStepKey } = coachState

      // Clear previous results
      setCoachState((prev) => ({
        ...prev,
        lastMCQOptions: [],
        lastCoaching: undefined,
        lastFacetsCreated: undefined,
      }))

      // Run assessment
      await executeStep(currentStepKey, 'assess', userAnswer, previousMessages)

      // Run followup for MCQ generation
      await executeStep(currentStepKey, 'followup', userAnswer, previousMessages)

      // Run extraction
      await executeStep(currentStepKey, 'extract', userAnswer, previousMessages)
    },
    [coachState, executeStep]
  )

  /**
   * Advance to the next step in the workflow
   */
  const advanceToNextStep = useCallback(async () => {
    if (!workflowData?.steps) return

    const { currentStepNumber, currentStepKey, completedSteps } = coachState
    const nextStepIndex = currentStepNumber // 0-indexed

    if (nextStepIndex >= workflowData.steps.length) {
      // Workflow complete
      setCoachState((prev) => ({
        ...prev,
        currentPhase: 'complete',
        completedSteps: [...completedSteps, currentStepKey],
      }))
      return
    }

    const nextStep = workflowData.steps[nextStepIndex]
    setCoachState((prev) => ({
      ...prev,
      currentStepKey: nextStep.key,
      currentStepNumber: nextStep.stepNumber,
      completedSteps: [...completedSteps, currentStepKey],
      lastAssessment: undefined,
      lastCoaching: undefined,
      lastMCQOptions: undefined,
      lastExtracted: undefined,
      lastFacetsCreated: undefined,
    }))

    // Start the next step
    await startStep(nextStep.key)
  }, [workflowData, coachState, startStep])

  /**
   * Reset the workflow to the beginning
   */
  const resetWorkflow = useCallback(() => {
    abortControllerRef.current?.abort()
    setCoachState({
      currentStepKey: 'understanding-context',
      currentStepNumber: 1,
      currentPhase: 'idle',
      completedSteps: [],
    })
    setStreamingState({ isStreaming: false, rawText: '' })
    setError(null)
  }, [])

  /**
   * Cancel ongoing request
   */
  const cancel = useCallback(() => {
    abortControllerRef.current?.abort()
    setStreamingState({ isStreaming: false, rawText: '' })
    setCoachState((prev) => ({ ...prev, currentPhase: 'awaiting_input' }))
  }, [])

  // Computed values
  const currentStep = workflowData?.steps.find(
    (s) => s.key === coachState.currentStepKey
  )

  const isWorkflowComplete = coachState.currentPhase === 'complete'
  const progress = workflowData?.steps
    ? (coachState.completedSteps.length / workflowData.steps.length) * 100
    : 0

  return {
    // Workflow data
    workflow: workflowData?.workflow,
    steps: workflowData?.steps || [],
    currentStep,
    isLoadingWorkflow,

    // State
    coachState,
    streamingState,
    error,
    isConfigured,

    // Computed
    isWorkflowComplete,
    progress,

    // Actions
    startStep,
    processAnswer,
    advanceToNextStep,
    resetWorkflow,
    cancel,

    // Direct step execution (for custom flows)
    executeStep,
  }
}

export default useCoachWorkflow
